import type { OnChangeValue, Option } from '@ksyos/design-system';
import { FormGroup, Select } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { getDisplayName } from '@common/utils/getDisplayName';
import { getValidationErrorTranslation } from '@frontend/utils';
import { getSupervisors } from '@frontend/utils/apis/getSupervisors';
import type { ValidationErrors } from '../../utils/validation';
import type { IPractitioner } from '../interfaces/practitioner';
import { translateKey } from '../../translate';

interface ISelectSupervisorProps {
    errors: ValidationErrors;
    onChangeSupervisor: (supervisorId: string | null, label: string | undefined) => Promise<void>;
    onSupervisorsAvailableAtInitialLoad?: (supervisorsAvailable: boolean) => void;
    organizationId: string;
    practitionerId: string;
    selectedSupervisor: string | null;
    selectedSupervisorLabel?: string | null;
}

export function SelectSupervisor(props: ISelectSupervisorProps) {
    const [supervisors, setSupervisors] = useState<{ label: string; value: string }[]>([]);
    const supervisorsAvailable = supervisors.length > 0;

    useEffect(() => {
        loadSupervisors();
    }, [props.organizationId]);

    useEffect(() => {
        if (props.onSupervisorsAvailableAtInitialLoad) {
            props.onSupervisorsAvailableAtInitialLoad(supervisorsAvailable);
        }
    }, [supervisorsAvailable]);

    const currentSupervisor = getCurrentSupervisor();

    return (
        <FormGroup
            hasErrors={props.errors.has('identifierDeclarationPractitionerId')}
            label={translateKey('supervisors.supervisor')}
            tooltipText={translateKey('supervisors.supervisorNeededInfoTooltipText')}
        >
            <Select
                error={getValidationErrorTranslation(props.errors.get('identifierDeclarationPractitionerId'))}
                isClearable={true}
                onChange={handleSupervisorChange}
                options={supervisors}
                placeholder={translateKey('identifierDeclarationOrRegistration.searchForNameOrAGB')}
                value={currentSupervisor}
            />
        </FormGroup>
    );

    function handleSupervisorChange(newValue: OnChangeValue<Option, false>) {
        props.onChangeSupervisor(newValue?.value || null, newValue?.label || undefined);
    }

    async function loadSupervisors(searchText?: string) {
        if (!props.organizationId) {
            return [];
        }

        const supervisorsResult = await getSupervisors(props.organizationId, props.practitionerId);
        let validSupervisorsWithLabel = supervisorsResult.map((item) => ({
            value: item.practitionerId,
            label: createLabel(item.practitioner),
        }));

        if (
            currentSupervisor &&
            !validSupervisorsWithLabel.some((supervisor) => supervisor.value === currentSupervisor.value)
        ) {
            validSupervisorsWithLabel = [...validSupervisorsWithLabel, currentSupervisor];
        }

        setSupervisors(validSupervisorsWithLabel);

        if (searchText) {
            const practitionersListSearchResult = validSupervisorsWithLabel.filter((item) =>
                item.label.toLocaleLowerCase().includes(searchText.toLocaleLowerCase())
            );

            return practitionersListSearchResult;
        }

        return validSupervisorsWithLabel;
    }

    function createLabel(practitioner: IPractitioner) {
        return `${getDisplayName(practitioner)} (${translateKey('identifierDeclarationOrRegistration.identifierDeclaration')}: ${
            practitioner.identifierDeclaration
        })`;
    }

    function getCurrentSupervisor() {
        if (!props.selectedSupervisor || !props.selectedSupervisorLabel) {
            return null;
        }

        return { value: props.selectedSupervisor, label: props.selectedSupervisorLabel };
    }
}
